import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Landmark, Gavel, Users, Home, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const roleTone = (title) => {
  const t = (title || "").toLowerCase();
  if (t.includes("chair") && !t.includes("vice") && !t.includes("ranking")) return "bg-orange-500/15 text-orange-200 border-orange-500/40";
  if (t.includes("ranking")) return "bg-blue-500/15 text-blue-200 border-blue-500/40";
  if (t.includes("vice")) return "bg-amber-500/15 text-amber-200 border-amber-500/40";
  return "bg-white/[0.06] text-white/70 border-white/15";
};

const SplitBar = ({ majority, minority }) => {
  const total = majority + minority || 1;
  return (
    <div className="h-2 rounded-full overflow-hidden flex bg-white/[0.06]" title={`${majority} majority seats, ${minority} minority seats`}>
      <div className="h-full bg-orange-400" style={{ width: `${(majority / total) * 100}%` }} />
      <div className="h-full bg-blue-400" style={{ width: `${(minority / total) * 100}%` }} />
    </div>
  );
};

const SeniorityBar = ({ rank, sideSize, side }) => {
  if (!rank || !sideSize) return null;
  const pct = ((sideSize - rank) / Math.max(sideSize - 1, 1)) * 100;
  return (
    <div title={`${ordinal(rank)} most senior of ${sideSize} ${side} members`}>
      <div className="flex justify-between text-[10px] text-gray-500 mb-1"><span>Junior</span><span>Senior</span></div>
      <div className="relative h-2 rounded-full bg-white/[0.06]">
        <span className="absolute -top-0.5 w-3 h-3 rounded-full bg-emerald-400" style={{ left: `calc(${pct}% - 6px)` }} />
      </div>
    </div>
  );
};

const CommitteeCard = ({ c, idx }) => (
  <Card className="p-5 bg-white rounded-xl border-2 border-gray-200" data-testid={`committee-card-${idx}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <Link to={`/committees/${encodeURIComponent(c.name)}`} className="text-xl font-bold text-gray-900 hover:text-blue-600 hover:underline inline-flex items-center gap-1" data-testid={`committee-link-${idx}`}>
          {c.shortName} <ChevronRight className="w-4 h-4 text-gray-400" />
        </Link>
        <p className="text-xs text-gray-500 mt-0.5">{c.name}</p>
      </div>
      <Badge className={`border ${roleTone(c.title)}`} data-testid={`committee-role-${idx}`}>{c.title}</Badge>
    </div>

    {c.jurisdiction && <p className="text-sm text-gray-600 mt-3 leading-relaxed" data-testid={`committee-jurisdiction-${idx}`}>{c.jurisdiction}</p>}

    <div className="grid sm:grid-cols-3 gap-4 mt-4">
      <div>
        <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Seniority</p>
        {c.rank && c.sideSize ? (
          <>
            <p className="text-sm font-bold text-gray-900" data-testid={`committee-seniority-${idx}`}>{ordinal(c.rank)} of {c.sideSize} on the {c.side}</p>
            <div className="mt-1.5"><SeniorityBar rank={c.rank} sideSize={c.sideSize} side={c.side} /></div>
          </>
        ) : <p className="text-sm text-gray-500">Not ranked</p>}
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Party split</p>
        <p className="text-sm font-bold text-gray-900">{c.size} members · {c.majority}-{c.minority}</p>
        <div className="mt-1.5"><SplitBar majority={c.majority} minority={c.minority} /></div>
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Member's bills here</p>
        <p className="text-sm font-bold text-gray-900" data-testid={`committee-member-bills-${idx}`}>{c.memberBillsHere} referred</p>
        <p className="text-[11px] text-gray-500 mt-0.5">of {c.referred}{c.referredCapped ? "+" : ""} bills sent to this committee this Congress</p>
      </div>
    </div>

    {c.subcommittees.length > 0 && (
      <div className="mt-4">
        <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">Subcommittee seats</p>
        <div className="flex flex-wrap gap-1.5">
          {c.subcommittees.map((s) => (
            <span key={s.name} className={`text-xs px-2 py-1 rounded-md border ${roleTone(s.title)}`} data-testid="committee-subcommittee-chip">
              {s.title !== "Member" ? `${s.title}, ` : ""}{s.name}
            </span>
          ))}
        </div>
      </div>
    )}

    {c.allies && c.allies.length > 0 && (
      <div className="mt-4 flex items-center gap-2 flex-wrap text-xs" data-testid={`committee-allies-${idx}`}>
        <span className="text-gray-500 inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Voting allies on this committee:</span>
        {c.allies.map((a) => (
          <Link key={a.legislatorId} to={`/legislator/${a.legislatorId}`} className="text-blue-600 hover:underline">{a.name} ({a.agreementPercent}%)</Link>
        ))}
      </div>
    )}
  </Card>
);

export const CommitteesTab = ({ legislatorId, legislator }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/legislators/${legislatorId}/committees`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [legislatorId]);

  if (loading) {
    return <Card className="p-6 bg-white rounded-xl border-2 border-gray-200" data-testid="committees-loading"><p className="text-gray-600 text-sm">Loading committee analytics…</p></Card>;
  }
  const committees = data?.committees || [];
  const ht = data?.homeTurf || {};
  const leadRoles = committees.filter((c) => c.title !== "Member").length
    + committees.reduce((n, c) => n + c.subcommittees.filter((s) => s.title !== "Member").length, 0);
  const lastName = (legislator?.name || "").split(" ").filter(Boolean).slice(-1)[0] || "This member";

  return (
    <div className="space-y-6" data-testid="profile-tab-panel-committees">
      {committees.length === 0 ? (
        <Card className="p-6 bg-white rounded-xl border-2 border-gray-200" data-testid="committees-empty">
          <h2 className="text-xl font-bold text-gray-900 mb-2">No standing committee assignments</h2>
          <p className="text-sm text-gray-600">Chamber leadership (Speaker, Majority and Minority Leaders, Whips) typically does not sit on standing committees.</p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="committees-summary">
            <div className="rounded-lg border border-gray-200 p-3 bg-white">
              <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1"><Landmark className="w-3 h-3" /> Committees</p>
              <p className="text-2xl font-black text-gray-900">{committees.length}</p>
              <p className="text-[11px] text-gray-500">{data.subcommitteeCount} subcommittee seat{data.subcommitteeCount === 1 ? "" : "s"}</p>
            </div>
            <div className="rounded-lg border border-gray-200 p-3 bg-white">
              <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1"><Gavel className="w-3 h-3" /> Gavels</p>
              <p className="text-2xl font-black text-gray-900">{leadRoles}</p>
              <p className="text-[11px] text-gray-500">chair, ranking or vice-chair roles</p>
            </div>
            <div className="rounded-lg border border-orange-500/40 bg-orange-500/10 p-3 col-span-2" data-testid="committees-home-turf">
              <p className="text-[11px] uppercase tracking-wider text-orange-300 font-semibold flex items-center gap-1"><Home className="w-3 h-3" /> Home turf</p>
              <p className="text-2xl font-black text-gray-900">{ht.pct != null ? `${ht.pct}%` : "n/a"}</p>
              <p className="text-[11px] text-gray-500">
                {ht.withReferral ? `${ht.toOwnCommittees} of ${ht.withReferral} bills ${lastName} sponsored went to a committee ${lastName} sits on` : "No referral data yet"}
                {ht.elsewhere?.length ? `. Elsewhere: ${ht.elsewhere.map((e) => `${e.committee} (${e.count})`).join(", ")}` : ""}
              </p>
            </div>
          </div>
          {committees.map((c, i) => <CommitteeCard key={c.name} c={c} idx={i} />)}
        </>
      )}
    </div>
  );
};
